import { useState, useEffect } from "react";
import { Trash2, Plus, ClipboardPaste, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BulkPasteDialog from "@/components/BulkPasteDialog";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const emptyCase = () => ({
  id: uid(),
  case_no: "",
  description: "",
  quantity: 1,
  length_cm: 0,
  width_cm: 0,
  height_cm: 0,
  weight_kg: 0,
  stackable: null,
});

// Source-unit factors → cm / kg. Used to re-interpret the numbers that came
// from the packing list when the auto-detection picked the wrong unit.
const DIM_FACTORS = { mm: 0.1, cm: 1, m: 100, in: 2.54, ft: 30.48 };
const WT_FACTORS = { g: 0.001, kg: 1, mt: 1000, lb: 0.453592 };

const selectCls =
  "bg-white border border-slate-200 mono text-[11px] uppercase tracking-widest px-2 h-8 text-slate-700 focus:outline-none focus:border-[#2563EB]";

export default function CargoTable({
  cases,
  onChange,
  sourceDimUnit = "cm",
  sourceWeightUnit = "kg",
}) {
  const FT = 1 / 30.48;
  const toFt = (cm) => (cm ? (cm * FT).toFixed(2) : "—");
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [dimSrc, setDimSrc] = useState(sourceDimUnit);
  const [wtSrc, setWtSrc] = useState(sourceWeightUnit);

  // Sync with a fresh extraction's detected units (no conversion — the backend
  // already normalised those values to cm / kg).
  useEffect(() => { setDimSrc(sourceDimUnit || "cm"); }, [sourceDimUnit]);
  useEffect(() => { setWtSrc(sourceWeightUnit || "kg"); }, [sourceWeightUnit]);

  const reinterpretDims = (next) => {
    const ratio = DIM_FACTORS[next] / DIM_FACTORS[dimSrc];
    setDimSrc(next);
    if (!ratio || ratio === 1) return;
    onChange(
      cases.map((c) => ({
        ...c,
        length_cm: Math.round(c.length_cm * ratio * 100) / 100,
        width_cm: Math.round(c.width_cm * ratio * 100) / 100,
        height_cm: Math.round(c.height_cm * ratio * 100) / 100,
      }))
    );
  };

  const reinterpretWeights = (next) => {
    const ratio = WT_FACTORS[next] / WT_FACTORS[wtSrc];
    setWtSrc(next);
    if (!ratio || ratio === 1) return;
    onChange(
      cases.map((c) => ({
        ...c,
        weight_kg: Math.round(c.weight_kg * ratio * 100) / 100,
      }))
    );
  };

  const handleBulkImport = (newCases) => {
    // Append after existing cases so users can mix manual + pasted rows.
    onChange([...cases, ...newCases]);
  };

  const update = (id, field, value) => {
    onChange(
      cases.map((c) =>
        c.id === id
          ? {
              ...c,
              [field]:
                field === "case_no" || field === "description"
                  ? value
                  : field === "stackable"
                  ? value
                  : Number(value) || 0,
              ...(field === "weight_kg" ? { weight_estimated: false } : {}),
            }
          : c
      )
    );
  };

  const removeRow = (id) => onChange(cases.filter((c) => c.id !== id));
  const addRow = () => onChange([...cases, emptyCase()]);

  const inputCls =
    "bg-transparent border-0 border-b border-slate-200 rounded-none focus:ring-0 focus:border-[#2563EB] mono text-sm px-2 h-9";

  const estimatedCount = cases.filter((c) => c.weight_estimated).length;

  return (
    <div className="bg-[#F8FAFC] border border-slate-200">
      {/* Source-unit bar — one-click override when the sheet used mm / m / ft / inch */}
      <div className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#64748B]">
          <Ruler className="w-3.5 h-3.5 text-[#2563EB]" /> Source units
        </div>
        <label className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Dimensions</span>
          <select
            data-testid="source-dim-unit-select"
            className={selectCls}
            value={dimSrc}
            onChange={(e) => reinterpretDims(e.target.value)}
            title="If the packing list numbers were in another unit, pick it here — all rows are re-converted instantly"
          >
            <option value="mm">MM</option>
            <option value="cm">CM</option>
            <option value="m">M</option>
            <option value="ft">FEET</option>
            <option value="in">INCH</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Weight</span>
          <select
            data-testid="source-weight-unit-select"
            className={selectCls}
            value={wtSrc}
            onChange={(e) => reinterpretWeights(e.target.value)}
            title="If the weights were actually in MT / lb / grams, pick it here — every row is re-converted to kg instantly"
          >
            <option value="kg">KG</option>
            <option value="mt">MT</option>
            <option value="lb">LB</option>
            <option value="g">GRAM</option>
          </select>
        </label>
        {estimatedCount > 0 && (
          <span
            data-testid="estimated-weight-note"
            className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-amber-300 bg-amber-50 text-amber-700"
          >
            {estimatedCount} row(s) weight estimated @ 250 kg/CBM
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cargo-table">
          <thead className="sticky top-0 bg-[#FFFFFF] z-10">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 overline">Case No.</th>
              <th className="text-left px-3 py-2 overline">Description</th>
              <th className="text-right px-3 py-2 overline">Qty</th>
              <th className="text-right px-3 py-2 overline">L-CM</th>
              <th className="text-right px-3 py-2 overline">W-CM</th>
              <th className="text-right px-3 py-2 overline">H-CM</th>
              <th className="text-right px-3 py-2 overline">L-FT</th>
              <th className="text-right px-3 py-2 overline">W-FT</th>
              <th className="text-right px-3 py-2 overline">H-FT</th>
              <th className="text-right px-3 py-2 overline">Wt (kg)</th>
              <th className="text-left px-3 py-2 overline">Stack</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => {
              return (
                <tr
                  key={c.id}
                  className="border-b border-slate-100 hover:bg-white/[0.03] transition-colors duration-150"
                  data-testid={`cargo-row-${c.id}`}
                >
                  <td className="px-2">
                    <Input
                      className={inputCls + " w-20"}
                      value={c.case_no}
                      onChange={(e) => update(c.id, "case_no", e.target.value)}
                    />
                  </td>
                  <td className="px-2">
                    <Input
                      className={inputCls + " min-w-[220px]"}
                      value={c.description}
                      onChange={(e) => update(c.id, "description", e.target.value)}
                    />
                  </td>
                  <td className="px-2">
                    <Input
                      type="number"
                      className={inputCls + " w-16 text-right"}
                      value={c.quantity}
                      onChange={(e) => update(c.id, "quantity", e.target.value)}
                    />
                  </td>
                  <td className="px-2">
                    <Input
                      type="number"
                      className={inputCls + " w-24 text-right"}
                      value={c.length_cm}
                      onChange={(e) => update(c.id, "length_cm", e.target.value)}
                    />
                  </td>
                  <td className="px-2">
                    <Input
                      type="number"
                      className={inputCls + " w-24 text-right"}
                      value={c.width_cm}
                      onChange={(e) => update(c.id, "width_cm", e.target.value)}
                    />
                  </td>
                  <td className="px-2">
                    <Input
                      type="number"
                      className={inputCls + " w-24 text-right"}
                      value={c.height_cm}
                      onChange={(e) => update(c.id, "height_cm", e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right mono text-[#64748B] text-xs">{toFt(c.length_cm)}</td>
                  <td className="px-3 py-2 text-right mono text-[#64748B] text-xs">{toFt(c.width_cm)}</td>
                  <td className="px-3 py-2 text-right mono text-[#64748B] text-xs">{toFt(c.height_cm)}</td>
                  <td className="px-2">
                    <div className="flex items-center justify-end gap-1">
                      <Input
                        type="number"
                        className={inputCls + " w-24 text-right"}
                        value={c.weight_kg}
                        onChange={(e) => update(c.id, "weight_kg", e.target.value)}
                      />
                      {c.weight_estimated && (
                        <span
                          data-testid={`weight-est-badge-${c.id}`}
                          className="mono text-[9px] uppercase px-1 py-0.5 border border-amber-300 bg-amber-50 text-amber-700"
                          title="No weight in the packing list — estimated from volume at 250 kg/CBM"
                        >
                          est
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2">
                    <select
                      data-testid={`stackable-select-${c.id}`}
                      className={selectCls}
                      value={c.stackable === true ? "yes" : c.stackable === false ? "no" : "auto"}
                      onChange={(e) =>
                        update(
                          c.id,
                          "stackable",
                          e.target.value === "yes" ? true : e.target.value === "no" ? false : null
                        )
                      }
                      title="Can other cases be stacked on top of this one? Auto = decided by weight (≤ 300 kg stackable)"
                    >
                      <option value="auto">Auto</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </td>
                  <td className="px-2 text-right">
                    <button
                      data-testid={`remove-row-${c.id}`}
                      onClick={() => removeRow(c.id)}
                      className="text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {cases.length === 0 && (
              <tr>
                <td colSpan={12} className="text-center py-8 text-[#64748B] text-sm">
                  No cases yet. Upload a packing list or add one manually.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-slate-200 flex flex-wrap justify-end gap-2">
        <Button
          data-testid="bulk-paste-btn"
          onClick={() => setShowBulkPaste(true)}
          variant="ghost"
          className="rounded-md border border-slate-200 hover:bg-slate-100 hover:text-[#2563EB] mono text-xs uppercase tracking-widest"
          title="Paste dimensions block from Excel and skip row-by-row typing"
        >
          <ClipboardPaste className="w-3.5 h-3.5 mr-1.5" /> Paste from Excel
        </Button>
        <Button
          data-testid="add-case-btn"
          onClick={addRow}
          variant="ghost"
          className="rounded-md border border-slate-200 hover:bg-slate-100 hover:text-[#2563EB] mono text-xs uppercase tracking-widest"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Case
        </Button>
      </div>

      <BulkPasteDialog
        show={showBulkPaste}
        onClose={() => setShowBulkPaste(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
}
